import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Lock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Provider Membership guard (founder directive 2026-09-16).
// ONE membership — 7-day free trial (card on file) → $19.95/month.
// Blocks the provider portal when the membership is inactive (trial over
// without payment, payment failed, cancelled and prepaid time exhausted).
// Exempt tenants (demo / billing-exempt / sovereign) always pass.
export default function TrialExpirationGuard({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [blocked, setBlocked] = useState(false);
  const [state, setState] = useState(null);

  const check = useCallback(async () => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    const companyId = localStorage.getItem('company_id');
    if (!token || !companyId) return;
    try {
      const { data } = await axios.get(`${API}/membership/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setState(data.state);
      setBlocked(data.access === false);
    } catch { /* never lock on network/auth errors */ }
  }, []);

  useEffect(() => { check(); }, [check, location.pathname]);

  if (blocked) {
    return (
      <div data-testid="membership-locked-screen" className="fixed inset-0 z-[999] bg-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {state === 'past_due' ? 'Payment issue with your membership' : 'Your membership is inactive'}
          </h1>
          <p className="text-gray-500 mb-6">
            {state === 'past_due'
              ? 'Your last $19.95 membership payment didn\u2019t go through. Update your card to keep using RemodDash — all your data is safe.'
              : 'To keep using RemodDash, activate your membership — $19.95/month for the entire platform. All your data and settings are preserved.'}
          </p>
          <Button
            data-testid="membership-locked-cta"
            onClick={() => navigate('/membership')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 h-11 rounded-xl"
          >
            {state === 'past_due' ? 'Update Payment' : 'Activate Membership'} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
